import PropTypes from 'prop-types';
import {useCallback} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import MessageType from '../../../enums/MessageType';
import Button from '../../../UI/Button/Button';
import SelectableContainer from '../../../UI/SelectableContainer/SelectableContainer';
import {addOrRemoveBasketItem} from '../../../v2/actions/OptionalBasketItemActions/addOrRemoveBasketItem';
import {redirect} from '../../../v2/api/redirect';
import Message from '../../../v2/components/ui/Message/Message';
import DynamicTruckIcon from '../../../v2/components/ui/svg/Components/DynamicTruckIcon';
import Translation from '../../../v2/lang/Translation';
import getEditBasketValidationMessage from '../../../v2/selectors/EditBasketSelectors/getEditBasketValidationMessage';
import getFirstSubscriptionBasketItem from '../../../v2/selectors/getFirstSubscriptionBasketItem';
import {
  getSelectedDeliveryCountry, getSiteCode, getSiteDataV2, isGuest,
} from '../../../v2/selectors/selectors';
import replaceHTMLEntityWithSymbol from '../../../v2/utils/replace-html-entity-with-symbol';
import useStyles from './OptionalDeliveryBasketItem.styles';

export function OptionalDeliveryBasketItem(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  const {
    optionalBasketItem,
    selectedDeliveryCountry,
    isGuestUser,
    addOrRemoveBasketItem,
    unsecureHomePageUrl,
    siteCode,
  } = props;

  // Helper function to format price display
  const formatPrice = useCallback(() => {
    if (!optionalBasketItem?.chargePricePerUnit) {
      return null;
    }
    
    const price = optionalBasketItem.chargePricePerUnit;
    if (price.formatted) {
      return replaceHTMLEntityWithSymbol(price.formatted);
    }
    
    // Fallback formatting if formatted price is not available
    if (price.value !== undefined && price.currencyCode) {
      return `${price.currencyCode} ${price.value}`;
    }
    
    return null;
  }, [optionalBasketItem?.chargePricePerUnit]);

  const handleAddToBasket = useCallback(() => {
    if (optionalBasketItem) {
      addOrRemoveBasketItem(optionalBasketItem.productId, 1, true);
    }
  }, [optionalBasketItem, addOrRemoveBasketItem]);
  
  const handleRemoveFromBasket = useCallback(() => {
    if (optionalBasketItem) {
      addOrRemoveBasketItem(optionalBasketItem.productId, 1, false);
    }
  }, [optionalBasketItem, addOrRemoveBasketItem]);
  
  const handleLoginRedirect = useCallback(() => {
    redirect.doRedirect(`${unsecureHomePageUrl}login.jsp?returnTo=${unsecureHomePageUrl}checkoutStart.account&redirect_behaviour=normal`);
  }, [unsecureHomePageUrl]);

  // Only render if there is an optional basket item
  if (!optionalBasketItem) {
    return null;
  }
  
  // Check if the current delivery country is restricted for this optional basket item
  const isCountryRestricted = () => {
    if (!optionalBasketItem?.restrictedCountryCodes?.length || !selectedDeliveryCountry) {
      return false;
    }
    return optionalBasketItem.restrictedCountryCodes.some(code => code.toLowerCase() === selectedDeliveryCountry.toLowerCase());
  };

  // If country is restricted, show info message instead of the basket item
  if (isCountryRestricted()) {
    return (
      <div className={classes.infoMessage}>
        <Message type={MessageType.INFO}>
          <Translation>premier.delivery.international.unavailable</Translation>
        </Message>
      </div>
    );
  }
  
  const getLinkProps = () => ({
    rel: 'external',
    target: '_self',
    href: '',
    'data-tag-type': 'a',
  });

  // If user is not signed in AND the optional basket item is a subscription product
  if (isGuestUser && optionalBasketItem.isSubscription) {
    return (
      <div className={classes.optionalDeliveryBasketItem}>
        <SelectableContainer
          backgroundColour={theme.optionalDeliveryBasketItem.backgroundColour}
          noBoxShadow
        >
          <div className={classes.optionalDeliveryBasketItemContents}>
            <div className={classes.content}>
              <div className={classes.information}>
                <div className={classes.icon}>
                  <DynamicTruckIcon siteCode={siteCode} />
                </div>
                <div className={classes.text}>
                  <h3 className={classes.title}>
                    <Translation>premier.delivery.title</Translation>
                  </h3>
                  <p className={classes.description}>
                    <Translation placeholderValues={formatPrice() ? [formatPrice()] : []}>
                      premier.delivery.description
                    </Translation>
                  </p>
                  <p className={classes.loginMessage}>
                    <Translation>premier.delivery.login.required</Translation>
                  </p>
                </div>
              </div>
              <Button
                id="OptionalDeliveryBasketItem__login-button"
                onClick={handleLoginRedirect}
                primary
                maxContent
                data-test="OptionalDeliveryBasketItem__login-button"
                className={classes.button}
              >
                <Translation optionalExtension={siteCode}>btn.sign.up.text</Translation>
              </Button>
            </div>
          </div>
          <p className={classes.descriptionExtra}>
            <Translation optionalExtension={siteCode}>
              premier.delivery.description.extra
            </Translation>
          </p>
          {props.editBasketValidationMessage?.messageTranslationKey && (
            <div className={classes.editBasketError} data-test="OptionalDeliveryBasketItem__error">
              <Message type={props.editBasketValidationMessage.messageType}>
                <Translation
                  optionalExtension={props.siteCode}
                  tagProps={[getLinkProps()]}
                >
                  {props.editBasketValidationMessage.messageTranslationKey}
                </Translation>
              </Message>
            </div>
          )}
        </SelectableContainer>
      </div>
    );
  }

  // If product is already in the basket, show remove button
  if (optionalBasketItem.isAdded) {
    return (
      <div className={classes.optionalDeliveryBasketItem}>
        <SelectableContainer
          backgroundColour={theme.optionalDeliveryBasketItem.backgroundColour}
          noBoxShadow
        >
          <div className={classes.optionalDeliveryBasketItemContents}>
            <div className={classes.content}>
              <div className={classes.information}>
                <div className={classes.icon}>
                  <DynamicTruckIcon siteCode={siteCode} />
                </div>
                <div className={classes.text}>
                  <h3 className={classes.title}>
                    <Translation>premier.delivery.title</Translation>
                  </h3>
                  <p className={classes.description}>
                    <Translation placeholderValues={formatPrice() ? [formatPrice()] : []}>
                      premier.delivery.description
                    </Translation>
                  </p>
                </div>
              </div>
              <Button
                onClick={handleRemoveFromBasket}
                primary
                data-test="OptionalDeliveryBasketItem__remove-button"
                className={classes.button}
              >
                <Translation>premier.delivery.remove</Translation>
              </Button>
            </div>
          </div>
          <p className={classes.descriptionExtra}>
            <Translation optionalExtension={siteCode}>
              premier.delivery.description.extra
            </Translation>
          </p>
          {props.editBasketValidationMessage?.messageTranslationKey && (
            <div className={classes.editBasketError} data-test="OptionalDeliveryBasketItem__error">
              <Message type={props.editBasketValidationMessage.messageType}>
                <Translation optionalExtension={props.siteCode} tagProps={[getLinkProps()]}>
                  {props.editBasketValidationMessage.messageTranslationKey}
                </Translation>
              </Message>
            </div>
          )}
        </SelectableContainer>
      </div>
    );
  }

  // Default case: Show add to basket button
  return (
    <div className={classes.optionalDeliveryBasketItem}>
      <SelectableContainer
        backgroundColour={theme.optionalDeliveryBasketItem.backgroundColour}
        noBoxShadow
      >
        <div className={classes.optionalDeliveryBasketItemContents}>
          <div className={classes.content}>
            <div className={classes.information}>
              <div className={classes.icon}>
                <DynamicTruckIcon siteCode={siteCode} />
              </div>
              <div className={classes.text}>
                <h3 className={classes.title}>
                  <Translation
                    optionalExtension={props.siteCode}
                  >
                    premier.delivery.title
                  </Translation>
                </h3>
                <p className={classes.description}>
                  <Translation
                    optionalExtension={props.siteCode}
                    placeholderValues={formatPrice() ? [formatPrice()] : []}
                  >
                    premier.delivery.description
                  </Translation>
                </p>
              </div>
            </div>
            <Button
              onClick={handleAddToBasket}
              primary
              data-test="OptionalDeliveryBasketItem__add-button"
              className={classes.button}
              optionalExtension={props.siteCode}
            >
              <Translation>premier.delivery.add</Translation>
            </Button>
          </div>
        </div>
        <p className={classes.descriptionExtra}>
          <Translation optionalExtension={siteCode}>
            premier.delivery.description.extra
          </Translation>
        </p>
        {props.editBasketValidationMessage?.messageTranslationKey && (
          <div className={classes.editBasketError} data-test="OptionalDeliveryBasketItem__error">
            <Message type={props.editBasketValidationMessage.messageType}>
              <Translation optionalExtension={props.siteCode} tagProps={[getLinkProps()]}>
                {props.editBasketValidationMessage.messageTranslationKey}
              </Translation>
            </Message>
          </div>
        )}
      </SelectableContainer>
    </div>
  );
}

OptionalDeliveryBasketItem.propTypes = {
  optionalBasketItem: PropTypes.shape({
    productId: PropTypes.number.isRequired,
    isSubscription: PropTypes.bool,
    isAdded: PropTypes.bool,
    restrictedCountryCodes: PropTypes.arrayOf(PropTypes.string),
    chargePricePerUnit: PropTypes.shape({
      value: PropTypes.number,
      currencyCode: PropTypes.string,
      formatted: PropTypes.string,
    }),
  }),
  selectedDeliveryCountry: PropTypes.string,
  isGuestUser: PropTypes.bool,
  addOrRemoveBasketItem: PropTypes.func.isRequired,
  unsecureHomePageUrl: PropTypes.string.isRequired,
  siteCode: PropTypes.string.isRequired,
  editBasketValidationMessage: PropTypes.shape({
    messageTranslationKey: PropTypes.string,
    messageType: PropTypes.string,
  }),
};

OptionalDeliveryBasketItem.defaultProps = {
  optionalBasketItem: null,
  selectedDeliveryCountry: '',
  isGuestUser: false,
};

const mapStateToProps = state => ({
  optionalBasketItem: getFirstSubscriptionBasketItem(state),
  selectedDeliveryCountry: getSelectedDeliveryCountry(state),
  isGuestUser: isGuest(state),
  unsecureHomePageUrl: getSiteDataV2(state).unsecureHomePageUrl,
  siteCode: getSiteCode(state),
  editBasketValidationMessage: getEditBasketValidationMessage(state),
});

const mapDispatchToProps = dispatch => ({
  addOrRemoveBasketItem: (productId, quantity, isAddToBasket) => dispatch(addOrRemoveBasketItem(productId, quantity, isAddToBasket)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OptionalDeliveryBasketItem);
